import { useState, useEffect, useCallback } from 'react';
import { getContributionReportCsvUrl } from '../utils/publicData';
import { splitCsvLine } from '../utils/csv';

export interface GenreData {
  name: string;
  amount: number;
}

export const useEcosystemData = () => {
  const [data, setData] = useState<GenreData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    // Bound the request so a stalled S3/CDN response degrades to the error
    // state instead of an indefinite loading spinner (now on the homepage).
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(getContributionReportCsvUrl(), { signal: controller.signal });
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const text = await response.text();
      const rows = text.split('\n').slice(1).filter(row => row.trim());
      
      if (rows.length === 0) {
        setData([]);
        setIsLoading(false);
        return;
      }

      // Get the most recent date from the first data row
      const firstRowDate = splitCsvLine(rows[0])[0];
      const mostRecentDate = new Date(firstRowDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
      
      // Calculate 30 days back from the most recent date
      const thirtyDaysAgo = new Date(mostRecentDate);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const genreMap = new Map<string, number>();

      rows.forEach(row => {
        const [dateStr, , amount, , , category] = splitCsvLine(row);
        if (!dateStr || !category) return;
        
        const rowDate = new Date(dateStr.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
        
        if (rowDate >= thirtyDaysAgo && category && (category !== 'Fees' && category !== 'Unknown')) {
          const currentAmount = genreMap.get(category) || 0;
          genreMap.set(category, currentAmount + Number(amount));
        }
      });

      setData(Array.from(genreMap, ([name, amount]) => ({ name, amount })));
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching ecosystem data:', err);
      setError('Failed to load ecosystem data');
      setIsLoading(false);
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error };
};
